/**
 * OparaYapsinHavuzPage - Fail-Rescue Havuzu (C4nav + B2-2)
 *
 * Staff (@personaajans.com) fail_rescue_pool kayitlarini gorur:
 *  - beklemede -> "Ustune Al" (claim)
 *  - devam_ediyor (kendi ustune aldigi) -> "Tamamla" -> per-reel manuel giris FORMU -> complete-rescue.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Loader2, RefreshCw, Instagram, Calendar, Hand, ArrowLeft, Plus, Trash2, Send, Eye, Heart, MessageCircle, Pin, Lock, Search } from "lucide-react";
import { toast } from 'sonner';

import NavTabs from '../components/NavTabs';
import Footer from '../components/Footer';
import { AnalysisResults } from '../components/HomePage';
import { mapV4ResultFromHistory } from '../hooks/useQueryHistory';

// B2-7: sol-bar avatar bas-harfleri (AnalizGecmisi getInitials deseni)
const getInitials = (username) => {
  if (!username) return '?';
  return username.replace(/^@/, '').slice(0, 2).toUpperCase();
};

// B2-3: tarih yok -> in_window (Son 90g penceresi, default Evet). Metrik-aktif <=> (!is_pinned && in_window).
// B2-4: shortcode YOK (backend rescue_N benzersiz uretir). B2-5: co_author (Konsey marka-adayi, metrik-kapatmadan bagimsiz).
const emptyReel = () => ({
  is_pinned: false, in_window: true, view_count: '',
  like_count: '', comment_count: '', like_hidden: false, co_author: '', caption: '',
});

// B2-4: binlik-virgul (formatCurrency HomePage:705 deseni). Display icin; payload'a RAW parseInt gider.
const fmtInt = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = parseInt(String(v).replace(/,/g, ''), 10);
  if (isNaN(n)) return '';
  return new Intl.NumberFormat('en-US').format(n);
};
const rawInt = (v) => String(v).replace(/,/g, '').replace(/[^\d]/g, '');

export default function OparaYapsinHavuzPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const { t, locale } = useTranslation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);  // HAVUZ-SILME: admin coklu-secim (original_task_id)
  const [deleting, setDeleting] = useState(false);
  const [stats, setStats] = useState([]);  // B2-5: kurtarma istatistigi [{user_id,name,count}]
  // B2-7: sol-bar kurtarma-gecmisi + secili rapor
  const [history, setHistory] = useState([]);
  const [historySearch, setHistorySearch] = useState('');  // client-side arama (username, spinner-tuzagi YOK)
  const [selectedRescue, setSelectedRescue] = useState(null);  // secili original_task_id
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // B2-2: form view-swap state
  const [mode, setMode] = useState('list');          // 'list' | 'form'
  const [activeItem, setActiveItem] = useState(null);
  const [followersCount, setFollowersCount] = useState('');
  const [isAuthority, setIsAuthority] = useState(false);
  const [formReels, setFormReels] = useState(() => Array.from({ length: 12 }, emptyReel));  // B2-5: 12-satir default
  const [submitting, setSubmitting] = useState(false);

  const isStaff = !!(user?.email && user.email.toLowerCase().endsWith('@personaajans.com'));

  const fetchPool = useCallback(async () => {
    if (!token) { setLoading(false); return; }  // cookie-mode/token-null: spinner sonsuz kalmasin
    setLoading(true);
    try {
      const res = await axios.get(`${API}/team/rescue-pool`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setItems([]);
      toast.error(t('rescue.load_error'));
    } finally {
      setLoading(false);
    }
    // deps'te t YOK -> t her render yeni fonksiyon; bagimli olmak sonsuz effect/fetch dongusu yaratir (spinner-tuzagi).
  }, [token]);

  // B2-5: kurtarma istatistigi (mount, staff-only). t deps'te YOK.
  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/team/rescue-stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setStats([]);
    }
  }, [token]);

  // B2-7: kurtarma-gecmisi (sol-bar). t deps'te YOK.
  const fetchHistory = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/team/rescue-history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setHistory([]);
    }
  }, [token]);

  // B2-7: sol-bar item tikla -> sagda o musterinin raporu. t deps'te YOK.
  const selectRescue = useCallback(async (originalTaskId) => {
    if (!token || !originalTaskId) return;
    setSelectedRescue(originalTaskId);
    setReport(null);
    setReportLoading(true);
    try {
      const res = await axios.get(`${API}/team/rescue-report/${originalTaskId}`, { headers: { Authorization: `Bearer ${token}` } });
      const rd = res.data?.result_data;
      // BUG2: ham result_data DOGRUDAN gecilmez -> AnalizGecmisi mapper'i (nested opara_core_metrikleri -> flat) uygula.
      const mapped = rd ? mapV4ResultFromHistory(rd, res.data?.username) : null;
      setReport(mapped ? { mapped, currency: res.data?.currency || null } : null);
    } catch (e) {
      setReport(null);
      toast.error(e.response?.data?.detail || t('rescue.report_error'));
    } finally {
      setReportLoading(false);
    }
    // eslint kurali kayitli degil -> disable-comment YOK; t stale-safe (locale degisince remount).
  }, [token]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/${locale}/`); return; }
    if (!isStaff) { toast.error(t('rescue.no_access')); navigate(`/${locale}/`); return; }
    fetchPool();
    fetchStats();
    fetchHistory();
    // deps'te t YOK, fetch* kararli ([token]) -> effect yalniz auth/staff/token degisince calisir.
  }, [authLoading, isStaff, token]);

  // B2-5 (#6 fix): isleniyor kayit varken 6sn auto-poll -> arka-plan bitince liste yenilenir, kayit duser.
  //   deps [hasProcessing, token] (fetch* kararli, t YOK) -> interval her render kurulmaz (spinner-tuzagi yok).
  const hasProcessing = items.some((it) => it.status === 'isleniyor');
  useEffect(() => {
    if (!hasProcessing || !token) return;
    const iv = setInterval(() => { fetchPool(); fetchStats(); fetchHistory(); }, 6000);
    return () => clearInterval(iv);
  }, [hasProcessing, token]);

  const handleClaim = async (originalTaskId) => {
    if (!token || !originalTaskId) return;
    setClaiming(originalTaskId);
    try {
      await axios.post(`${API}/team/claim-rescue/${originalTaskId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('rescue.claimed_ok'));
      await fetchPool();
    } catch (e) {
      const status = e.response?.status;
      if (status === 409) {
        toast.error(e.response?.data?.detail || t('rescue.already_claimed'));
        await fetchPool();
      } else {
        toast.error(e.response?.data?.detail || t('rescue.claim_error'));
      }
    } finally {
      setClaiming(null);
    }
  };

  // HAVUZ-SILME (D1-D5): admin coklu-secim + KALICI silme. window.confirm (AdminPage.js:452/:556 emsali, yeni modal YOK).
  const toggleSelect = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const handleDeleteSelected = async () => {
    if (!token || selectedIds.length === 0) return;
    // devam_ediyor uyarisi: acik formu olan kayitlar (assigned_to_name ile).
    const inProgress = items.filter((it) => selectedIds.includes(it.original_task_id) && (it.status === 'devam_ediyor' || it.status === 'isleniyor'));
    let msg = t('rescue.delete_confirm', { count: selectedIds.length });
    if (inProgress.length > 0) {
      const names = inProgress.map((it) => it.assigned_to_name || t('rescue.team_fallback')).join(', ');
      msg += '\n\n' + t('rescue.delete_in_progress_warn', { count: inProgress.length, names });
    }
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/team/rescue-pool/delete`, { ids: selectedIds }, { headers: { Authorization: `Bearer ${token}` } });
      const deleted = res.data?.deleted ?? 0;
      const nf = res.data?.not_found?.length || 0;
      // 11.13: deleted==0 iken "silindi" yalan olur -> yalniz not_found uyarisi. Ikisi de varsa AYRI toast'lar.
      if (deleted > 0) toast.success(t('rescue.delete_success', { count: deleted }));
      if (nf > 0) toast.error(t('rescue.delete_not_found', { count: nf }));
      setSelectedIds([]);
      await fetchPool();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('rescue.delete_error'));
    } finally {
      setDeleting(false);
    }
  };

  const openForm = (item) => {
    setActiveItem(item);
    // REGRESYON GUVENCESI: once bos kurulum (cache yoksa/hata olursa mevcut davranis).
    setFollowersCount('');
    setIsAuthority(false);
    setFormReels(Array.from({ length: 12 }, emptyReel));  // B2-6: form her acilista 12 satir
    setMode('form');
    // 48s CACHE ON-DOLDURMA: staff son-veriyi 48s icinde re-fail'de geri getir. openForm-ici fetch (YENI effect YOK; t deps'te YOK).
    if (item?.username && token) {
      (async () => {
        try {
          const res = await axios.get(`${API}/team/rescue-manual-cache/${encodeURIComponent(item.username)}`,
            { headers: { Authorization: `Bearer ${token}` } });
          if (res.data?.found && Array.isArray(res.data.reels) && res.data.reels.length > 0) {
            // ROUND-TRIP: cache.reels alan-adlari emptyReel ile birebir; sayilari string'e normalize (form-state string tutar).
            const _mapped = res.data.reels.map((r) => ({
              is_pinned: !!r.is_pinned,
              in_window: r.in_window !== undefined ? !!r.in_window : true,
              view_count: r.view_count === 0 || r.view_count ? String(r.view_count) : '',
              like_count: r.like_count === 0 || r.like_count ? String(r.like_count) : '',
              comment_count: r.comment_count === 0 || r.comment_count ? String(r.comment_count) : '',
              like_hidden: !!r.like_hidden,
              co_author: r.co_author || '',
              caption: r.caption || '',
            }));
            setFormReels(_mapped);
            if (res.data.followers_count !== undefined && res.data.followers_count !== null) {
              setFollowersCount(String(res.data.followers_count));
            }
            setIsAuthority(!!res.data.is_authority);
            toast.success(t('rescue.cache_prefilled'));
          }
        } catch (e) {
          /* cache yoksa/hata -> bos form (regresyon yok) */
        }
      })();
    }
  };
  const closeForm = () => { setMode('list'); setActiveItem(null); };

  const updateReel = (idx, field, value) => {
    setFormReels((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addReel = () => setFormReels((prev) => (prev.length < 12 ? [...prev, emptyReel()] : prev));
  const removeReel = (idx) => setFormReels((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!token || !activeItem) return;
    const fc = parseInt(rawInt(followersCount), 10);
    if (!fc || fc <= 0) { toast.error(t('rescue.form_followers_required')); return; }

    // Bos-olmayan satirlari topla (display index korunur -> "Reel #N" staff'in gordugu satirla eslesir)
    const nonEmpty = [];
    formReels.forEach((r, displayIdx) => {
      const hasAny = r.view_count || r.is_pinned || !r.in_window || r.like_count || r.comment_count || r.caption.trim() || r.co_author.trim();
      if (hasAny) nonEmpty.push({ r, displayIdx });
    });
    if (nonEmpty.length === 0) { toast.error(t('rescue.form_min_one_reel')); return; }

    // Validasyon (B2-4: shortcode YOK): metrik-aktif reel (!is_pinned && in_window) -> yalniz view>0. En az 1 metrik-aktif.
    let activeCount = 0;
    for (const { r, displayIdx } of nonEmpty) {
      const metricActive = !r.is_pinned && r.in_window;
      if (!metricActive) continue;  // pinned/pencere-disi: caption-only, metrik zorunlu degil
      activeCount += 1;
      if (!r.view_count || parseInt(rawInt(r.view_count), 10) <= 0) {
        toast.error(t('rescue.form_reel_missing', { n: displayIdx + 1, fields: t('rescue.col_views') }));
        return;
      }
    }
    if (activeCount === 0) { toast.error(t('rescue.form_no_active_reel')); return; }

    const _n = (v) => (v ? parseInt(rawInt(v), 10) || 0 : 0);
    const payload = {
      followers_count: fc,
      is_authority: isAuthority,
      reels: nonEmpty.map(({ r }) => ({
        is_pinned: r.is_pinned,
        in_window: !r.is_pinned && r.in_window,  // pinned -> pencere anlamsiz
        view_count: _n(r.view_count),
        like_count: _n(r.like_count),
        comment_count: _n(r.comment_count),
        like_hidden: r.like_hidden,
        co_author: r.co_author || '',  // B2-5: Konsey marka-adayi (backend coauthor_producers)
        caption: r.caption || '',
      })),
    };

    setSubmitting(true);
    try {
      await axios.post(`${API}/team/complete-rescue/${activeItem.original_task_id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('rescue.submit_accepted'));  // B2-4 async 202: "Talebiniz alindi, tamamlaninca e-posta ile"
      closeForm();
      await fetchPool();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('rescue.complete_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) {
      return iso;
    }
  };

  if (authLoading || !isStaff) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F97316] animate-spin" />
      </div>
    );
  }

  // BUG1: 3 grup. beklemede -> Ustune Al; benim aktifim -> Tamamla/Isleniyor; baskasinin aktifi -> "{isim} uzerinde" (kilit).
  const _uid = user?.id;
  // HAVUZ-SILME (D1): YALNIZ role=='admin' (Bora/Orkun). Backend zaten korur; UI de gizler (gosterilen != cekilen onlemi).
  const isAdmin = user?.role === 'admin';
  const _active = (it) => it.status === 'devam_ediyor' || it.status === 'isleniyor';
  const pending = items.filter((it) => it.status === 'beklemede');
  const mine = items.filter((it) => _active(it) && it.assigned_to_id === _uid);
  const team = items.filter((it) => _active(it) && it.assigned_to_id !== _uid);

  // ── FORM GORUNUMU (view-swap) ──
  if (mode === 'form' && activeItem) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col">
        <NavTabs user={user} currentPage="oparayapsin" />
        <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-6">
            <Button onClick={closeForm} variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800">
              <ArrowLeft className="w-4 h-4 mr-1" />{t('rescue.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold">{t('rescue.form_title')}</h1>
              <p className="text-lg font-bold text-[#F97316] mt-0.5">{t('rescue.influencer_label')}: @{activeItem.username}</p>
              <p className="text-xs text-gray-500">{activeItem.wall_category || activeItem.error_type || t('rescue.reason_unknown')}</p>
            </div>
          </div>

          {/* Hesap-basi alanlar (izlenme binlik-virgul: type=text + fmtInt display + rawInt onChange) */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('rescue.form_followers')} *</label>
              <Input
                type="text"
                inputMode="numeric"
                value={fmtInt(followersCount)}
                onChange={(e) => setFollowersCount(rawInt(e.target.value))}
                placeholder="0"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <label className="flex items-center gap-2 mt-5 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" checked={isAuthority} onChange={(e) => setIsAuthority(e.target.checked)} className="w-4 h-4 accent-[#F97316]" />
              {t('rescue.form_is_authority')}
            </label>
          </div>

          {/* Reel INPUT-TABLOSU (AdminPage ReelsSnapshotModal:430 stili). B2-5: co-author kolonu + gizli-mi ayri kolon + satir -%40 (12 reel tek bakista) */}
          <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700 hover:bg-gray-800/50">
                  <TableHead className="text-gray-400 font-semibold w-10 py-1">#</TableHead>
                  <TableHead className="text-gray-400 font-semibold text-center whitespace-nowrap py-1">{t('rescue.window_q')}</TableHead>
                  <TableHead className="text-gray-400 font-semibold text-center py-1">
                    <div className="flex items-center gap-1 justify-center"><Pin className="w-3 h-3" /> {t('rescue.col_pinned')}</div>
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold text-right py-1">
                    <div className="flex items-center gap-1 justify-end"><Eye className="w-3 h-3" /> {t('rescue.col_views')}</div>
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold text-right py-1">
                    <div className="flex items-center gap-1 justify-end"><Heart className="w-3 h-3" /> {t('rescue.col_likes')}</div>
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold text-center whitespace-nowrap py-1">{t('rescue.col_hidden_q')}</TableHead>
                  <TableHead className="text-gray-400 font-semibold text-right py-1">
                    <div className="flex items-center gap-1 justify-end"><MessageCircle className="w-3 h-3" /> {t('rescue.col_comments')}</div>
                  </TableHead>
                  <TableHead className="text-gray-400 font-semibold py-1 min-w-[140px]">{t('rescue.col_coauthor')}</TableHead>
                  <TableHead className="text-gray-400 font-semibold py-1 min-w-[180px]">{t('rescue.col_caption')}</TableHead>
                  <TableHead className="text-gray-400 font-semibold w-8 py-1"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formReels.map((r, idx) => {
                  const metricActive = !r.is_pinned && r.in_window;
                  const off = 'opacity-40 pointer-events-none';
                  return (
                    <TableRow key={idx} className="border-gray-700 hover:bg-gray-800/50 align-top">
                      <TableCell className="text-[#F97316] font-mono font-semibold py-1">{idx + 1}</TableCell>
                      <TableCell className="text-center py-1">
                        {/* B2-6: 90-gun toggle HER reel'de gorunur/aktif (Sabit'ten decouple; metrik-gating ayri: metricActive=!is_pinned&&in_window) */}
                        <div className="inline-flex rounded overflow-hidden border border-gray-700">
                          <button type="button" onClick={() => updateReel(idx, 'in_window', true)}
                            className={`px-2 py-0.5 text-xs ${r.in_window ? 'bg-[#F97316] text-black font-bold' : 'bg-gray-800 text-gray-400'}`}>{t('rescue.yes')}</button>
                          <button type="button" onClick={() => updateReel(idx, 'in_window', false)}
                            className={`px-2 py-0.5 text-xs ${!r.in_window ? 'bg-[#F97316] text-black font-bold' : 'bg-gray-800 text-gray-400'}`}>{t('rescue.no')}</button>
                        </div>
                      </TableCell>
                      <TableCell className="text-center py-1">
                        {idx < 3 ? (
                          <input type="checkbox" checked={r.is_pinned} onChange={(e) => updateReel(idx, 'is_pinned', e.target.checked)} className="w-4 h-4 accent-[#F97316]" />
                        ) : <span className="text-gray-700">—</span>}
                      </TableCell>
                      <TableCell className="py-1">
                        <Input type="text" inputMode="numeric" disabled={!metricActive}
                          value={fmtInt(r.view_count)} onChange={(e) => updateReel(idx, 'view_count', rawInt(e.target.value))}
                          className={`bg-gray-800 border-gray-700 text-white h-6 text-xs text-right w-20 ${!metricActive ? off : ''}`} />
                      </TableCell>
                      <TableCell className="py-1">
                        <Input type="text" inputMode="numeric" disabled={!metricActive || r.like_hidden}
                          value={fmtInt(r.like_count)} onChange={(e) => updateReel(idx, 'like_count', rawInt(e.target.value))}
                          className={`bg-gray-800 border-gray-700 text-white h-6 text-xs text-right w-20 ${(!metricActive || r.like_hidden) ? off : ''}`} />
                      </TableCell>
                      <TableCell className="text-center py-1">
                        {!metricActive ? <span className="text-gray-700">—</span> : (
                          <div className="inline-flex rounded overflow-hidden border border-gray-700">
                            <button type="button" onClick={() => updateReel(idx, 'like_hidden', true)}
                              className={`px-2 py-0.5 text-xs ${r.like_hidden ? 'bg-[#F97316] text-black font-bold' : 'bg-gray-800 text-gray-400'}`}>{t('rescue.yes')}</button>
                            <button type="button" onClick={() => updateReel(idx, 'like_hidden', false)}
                              className={`px-2 py-0.5 text-xs ${!r.like_hidden ? 'bg-[#F97316] text-black font-bold' : 'bg-gray-800 text-gray-400'}`}>{t('rescue.no')}</button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-1">
                        <Input type="text" inputMode="numeric" disabled={!metricActive}
                          value={fmtInt(r.comment_count)} onChange={(e) => updateReel(idx, 'comment_count', rawInt(e.target.value))}
                          className={`bg-gray-800 border-gray-700 text-white h-6 text-xs text-right w-20 ${!metricActive ? off : ''}`} />
                      </TableCell>
                      <TableCell className="py-1">
                        {/* Co-author HER reel'de AKTIF (metrik-kapatmadan bagimsiz; pinned/pencere-disi reel'de de marka olabilir) */}
                        <Input type="text" value={r.co_author} onChange={(e) => updateReel(idx, 'co_author', e.target.value)}
                          placeholder={t('rescue.coauthor_hint')} className="bg-gray-800 border-gray-700 text-white h-6 text-xs w-full" />
                      </TableCell>
                      <TableCell className="py-1">
                        <Textarea value={r.caption} onChange={(e) => updateReel(idx, 'caption', e.target.value)}
                          placeholder={t('rescue.caption_hint')} rows={1}
                          className="bg-gray-800 border-gray-700 text-white text-xs min-h-[24px] w-full py-1" />
                      </TableCell>
                      <TableCell className="text-center py-1">
                        <button onClick={() => removeReel(idx)} className="text-red-400 hover:text-red-300" title={t('rescue.col_remove')}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {formReels.length < 12 && (
            <div className="flex justify-center mt-3">
              <Button onClick={addReel} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800">
                <Plus className="w-4 h-4 mr-1" />{t('rescue.form_add_reel')}
              </Button>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#F97316] hover:bg-[#EA6A0C] text-black font-bold h-11 px-6">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {submitting ? t('rescue.form_submitting') : t('rescue.form_submit')}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ── LISTE GORUNUMU ──
  const renderRow = (it) => (
    <div key={it.original_task_id} className="bg-gray-900 border border-gray-800 rounded-lg p-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        {isAdmin && (
          <input
            type="checkbox"
            checked={selectedIds.includes(it.original_task_id)}
            onChange={() => toggleSelect(it.original_task_id)}
            className="w-4 h-4 accent-red-600 cursor-pointer flex-shrink-0"
            title={t('rescue.delete')}
          />
        )}
        <div className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0">
          <Instagram className="w-4 h-4 text-[#F97316]" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold truncate">@{it.username || '—'}</span>
            {it.is_bulk && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-300 border border-blue-800">{t('rescue.bulk_tag')}</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
            <span className="truncate">{it.wall_category || it.error_type || t('rescue.reason_unknown')}</span>
            <span className="flex items-center gap-1 flex-shrink-0"><Calendar className="w-3 h-3" />{formatDate(it.created_at)}</span>
          </div>
        </div>
      </div>
      {it.status === 'beklemede' ? (
        <Button onClick={() => handleClaim(it.original_task_id)} disabled={claiming === it.original_task_id} className="bg-[#F97316] hover:bg-[#EA6A0C] text-black font-bold flex-shrink-0">
          {claiming === it.original_task_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Hand className="w-4 h-4 mr-2" />}
          {t('rescue.claim_btn')}
        </Button>
      ) : it.assigned_to_id !== _uid ? (
        /* BUG1: baskasinin aktifi -> kilit, tiklanamaz */
        <span className="flex items-center gap-2 text-sm text-gray-400 font-semibold flex-shrink-0 bg-gray-800 border border-gray-700 rounded-md px-3 py-1.5">
          <Lock className="w-4 h-4" />{t('rescue.team_working', { name: it.assigned_to_name || t('rescue.team_fallback') })}
        </span>
      ) : it.status === 'isleniyor' ? (
        <span className="flex items-center gap-2 text-sm text-amber-400 font-semibold flex-shrink-0">
          <Loader2 className="w-4 h-4 animate-spin" />{t('rescue.status_processing')}
        </span>
      ) : (
        <Button onClick={() => openForm(it)} className="bg-[#F97316] hover:bg-[#EA6A0C] text-black font-bold flex-shrink-0">
          <Send className="w-4 h-4 mr-2" />
          {t('rescue.complete_btn_named', { name: it.assigned_to_name || t('rescue.team_fallback') })}
        </Button>
      )}
    </div>
  );

  // B2-7: sol-bar kurtarma-gecmisi item (AnalizGecmisi HistoryItem stili)
  const renderHistoryItem = (h) => {
    const active = selectedRescue === h.original_task_id;
    return (
      <div key={h.original_task_id} onClick={() => selectRescue(h.original_task_id)}
        className={`flex gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
          active ? 'bg-[#FFDD59]/10 border-[#FFDD59]/50' : 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-800 hover:border-gray-600'}`}>
        <div className="shrink-0 w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center border-2 border-gray-600">
          <span className="font-bold text-[#FFDD59] text-xs">{getInitials(h.username)}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-white truncate text-sm">@{h.username || '—'}</h4>
            <span className="text-gray-500 whitespace-nowrap ml-2 text-[11px]">{formatDate(h.completed_at)}</span>
          </div>
          <p className="text-xs text-gray-400 truncate mt-0.5">
            {t('rescue.rescued_by', { name: h.completed_by_name || t('rescue.team_fallback') })}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <NavTabs user={user} currentPage="oparayapsin" />

      <div className="flex-1 flex flex-col lg:flex-row lg:items-start lg:gap-4 lg:max-w-[1600px] lg:mx-auto w-full px-4 py-6">
        {/* SOL: Kurtarma gecmisi (AnalizGecmisi:332 sol-bar deseni) - 90g pencere (backend) + client arama */}
        <aside className="w-full lg:w-80 xl:w-96 lg:flex-shrink-0 mb-4 lg:mb-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">{t('rescue.rescue_history_title')}</h2>
            <span className="text-[11px] text-gray-500">Son 3 Ay</span>
          </div>
          {/* Arama (client-side, username; yeni fetch YOK) */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input type="text" value={historySearch} onChange={(e) => setHistorySearch(e.target.value)}
              placeholder="Influencer ara..." className="pl-10 bg-gray-800 border-gray-700 text-white text-sm h-9" />
          </div>
          {(() => {
            const _q = historySearch.trim().toLowerCase();
            const _filtered = _q ? history.filter((h) => (h.username || '').toLowerCase().includes(_q)) : history;
            if (history.length === 0) {
              return (
                <div className="bg-gray-900 border border-gray-800 rounded-lg py-10 text-center">
                  <p className="text-gray-500 text-sm">{t('rescue.no_rescue_yet')}</p>
                </div>
              );
            }
            if (_filtered.length === 0) {
              return (
                <div className="bg-gray-900 border border-gray-800 rounded-lg py-10 text-center">
                  <p className="text-gray-500 text-sm">Sonuç yok</p>
                </div>
              );
            }
            return (
              <div className="space-y-2 lg:max-h-[720px] lg:overflow-y-auto pr-1">
                {_filtered.map(renderHistoryItem)}
              </div>
            );
          })()}
        </aside>

        {/* SAG: mevcut havuz + secili rapor */}
        <main className="flex-1 min-w-0">
          {selectedRescue ? (
            /* Secili kurtarma raporu */
            <div>
              <Button onClick={() => { setSelectedRescue(null); setReport(null); }} variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:bg-gray-800 mb-4">
                <ArrowLeft className="w-4 h-4 mr-1" />{t('rescue.back')}
              </Button>
              {reportLoading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#F97316] animate-spin" /></div>
              ) : report?.mapped ? (
                <div className="bg-gray-900/50 rounded-2xl border border-gray-800">
                  <div className="max-w-[50rem] mx-auto p-2 sm:p-4">
                    <AnalysisResults result={report.mapped} currency={report.currency || null} />
                  </div>
                </div>
              ) : (
                <div className="bg-gray-900 border border-gray-800 rounded-lg py-16 text-center">
                  <p className="text-gray-400">{t('rescue.report_error')}</p>
                </div>
              )}
            </div>
          ) : (
            /* Havuz gorunumu */
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-7 h-7 text-[#F97316]" />
                  <div>
                    <h1 className="text-2xl font-bold">{t('rescue.title')}</h1>
                    <p className="text-sm text-gray-400">{t('rescue.subtitle')}</p>
                  </div>
                </div>
                <Button onClick={fetchPool} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800" disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{t('rescue.refresh')}
                </Button>
              </div>

              {/* B2-5: Kurtarma istatistigi (bos ise gizli) */}
              {stats.length > 0 && (
                <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-2.5 mb-6 flex items-center gap-3 flex-wrap">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">{t('rescue.stats_title')}</span>
                  {stats.map((s) => (
                    <span key={s.user_id} className="text-sm text-gray-300">
                      <span className="text-[#F97316] font-semibold">{s.name}</span>: {s.count}
                    </span>
                  ))}
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-6 h-6 text-[#F97316] animate-spin" />
                </div>
              ) : items.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded-lg py-16 text-center">
                  <p className="text-gray-400">{t('rescue.empty')}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {isAdmin && selectedIds.length > 0 && (
                    <div className="flex items-center justify-between bg-red-950/30 border border-red-900 rounded-lg px-4 py-3">
                      <span className="text-sm text-red-300">{t('rescue.delete_selected', { count: selectedIds.length })}</span>
                      <Button onClick={handleDeleteSelected} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white font-bold flex-shrink-0">
                        {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        {t('rescue.delete')}
                      </Button>
                    </div>
                  )}
                  {mine.length > 0 && (
                    <div>
                      <h2 className="text-sm font-semibold text-[#F97316] mb-2 uppercase tracking-wide">{t('rescue.section_mine')} ({mine.length})</h2>
                      <div className="space-y-2">{mine.map((it) => renderRow(it))}</div>
                    </div>
                  )}
                  {team.length > 0 && (
                    <div>
                      <h2 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">{t('rescue.section_team')} ({team.length})</h2>
                      <div className="space-y-2">{team.map((it) => renderRow(it))}</div>
                    </div>
                  )}
                  {pending.length > 0 && (
                    <div>
                      <h2 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wide">{t('rescue.section_pending')} ({pending.length})</h2>
                      <div className="space-y-2">{pending.map((it) => renderRow(it))}</div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
}
